// src/components/persons/PersonLocations/PersonLocationForm.jsx
import React, { useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControlLabel,
    Switch,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PersonLocationMap } from './PersonLocationMap';
import { LOCATION_TYPE_OPTIONS } from '../../utils/persons/locationTypes';

const schema = yup.object({
    locnom: yup
        .string()
        .required('El nombre de la dirección es obligatorio')
        .max(100, 'Máximo 100 caracteres'),
    locdes: yup.string().nullable().max(255, 'Máximo 255 caracteres'),
    loclat: yup
        .number()
        .typeError('Latitud inválida')
        .min(-90, 'Latitud debe estar entre -90 y 90')
        .max(90, 'Latitud debe estar entre -90 y 90')
        .required('Latitud obligatoria'),
    loclng: yup
        .number()
        .typeError('Longitud inválida')
        .min(-180, 'Longitud debe estar entre -180 y 180')
        .max(180, 'Longitud debe estar entre -180 y 180')
        .required('Longitud obligatoria'),
    perloctip: yup.string().required('El tipo es obligatorio'),
    perlocpri: yup.boolean().default(false),
    perlocdes: yup.string().nullable().max(255, 'Máximo 255 caracteres'),
});

export const PersonLocationForm = ({
    open,
    onClose,
    onSubmit,
    defaultValues = null,
    isSubmitting = false,
}) => {
    const isEdit = Boolean(defaultValues?.locid);
    const editingLocId = defaultValues?.locid; // ✅ Guardar el ID para edición

    const initialValues = useMemo(
        () => ({
            locnom: defaultValues?.locnom || '',
            locdes: defaultValues?.locdes || '',
            loclat: defaultValues?.loclat ?? -16.3989,
            loclng: defaultValues?.loclng ?? -71.535,
            perloctip: defaultValues?.perloctip || 'CASA_PRINCIPAL',
            perlocpri: defaultValues?.perlocpri === true,
            perlocdes: defaultValues?.perlocdes || '',
        }),
        [defaultValues]
    );

    const {
        register,
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isDirty },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
        mode: 'onChange',
    });

    const loclat = watch('loclat');
    const loclng = watch('loclng');

    useEffect(() => {
        if (open) {
            reset({
                locnom: defaultValues?.locnom || '',
                locdes: defaultValues?.locdes || '',
                loclat: defaultValues?.loclat ?? -16.3989,
                loclng: defaultValues?.loclng ?? -71.535,
                perloctip: defaultValues?.perloctip || 'CASA_PRINCIPAL',
                perlocpri: defaultValues?.perlocpri === true,
                perlocdes: defaultValues?.perlocdes || '',
            });
        }
    }, [open, reset, defaultValues]);

    const handleCoordinatesChange = (lng, lat) => {
        setValue('loclng', lng, { shouldDirty: true, shouldValidate: true });
        setValue('loclat', lat, { shouldDirty: true, shouldValidate: true });
    };

    const submit = (data) => {
        // ✅ En edición, incluir el locId
        const payload = {
            locnom: data.locnom.trim(),
            locdes: data.locdes?.trim() || null,
            loclat: Number(data.loclat),
            loclng: Number(data.loclng),
            perloctip: data.perloctip,
            perlocpri: data.perlocpri,
            perlocdes: data.perlocdes?.trim() || null,
        };

        if (isEdit && editingLocId) {
            payload.locId = editingLocId; // ✅ Agregar el ID para edición
        }

        onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isEdit ? 'Editar dirección' : 'Nueva dirección'}
            </DialogTitle>

            <Divider />

            <form onSubmit={handleSubmit(submit)}>
                <DialogContent>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={7}>
                            <TextField
                                label="Nombre de la dirección *"
                                fullWidth
                                size="small"
                                {...register('locnom')}
                                error={!!errors.locnom}
                                helperText={errors.locnom?.message || 'Ej: Casa principal, Oficina central'}
                            />
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <TextField
                                select
                                label="Tipo *"
                                fullWidth
                                size="small"
                                {...register('perloctip')}
                                error={!!errors.perloctip}
                                helperText={errors.perloctip?.message}
                            >
                                {LOCATION_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Descripción de la ubicación"
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                {...register('locdes')}
                                error={!!errors.locdes}
                                helperText={errors.locdes?.message || 'Referencia geográfica general'}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Haz clic en el mapa, arrastra el marcador o busca una dirección
                            </Typography>

                            <PersonLocationMap
                                initialLng={loclng}
                                initialLat={loclat}
                                onCoordinatesChange={handleCoordinatesChange}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Latitud"
                                fullWidth
                                type="number"
                                size="small"
                                inputProps={{ step: '0.000001' }}
                                {...register('loclat')}
                                error={!!errors.loclat}
                                helperText={errors.loclat?.message}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                label="Longitud"
                                fullWidth
                                type="number"
                                size="small"
                                inputProps={{ step: '0.000001' }}
                                {...register('loclng')}
                                error={!!errors.loclng}
                                helperText={errors.loclng?.message}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="perlocpri"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label="Marcar como dirección principal para taxi"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Nota personal"
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                {...register('perlocdes')}
                                error={!!errors.perlocdes}
                                helperText={
                                    errors.perlocdes?.message || 'Ej: Casa de mi mamá, portón azul, timbre 3B'
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} disabled={isSubmitting}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !isDirty}
                    >
                        {isEdit ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};